import EmptyState from "../../components/EmptyState";
import type { HistoryEntry } from "../../api/types";
import HistoryRow from "./HistoryRow";
import type { ReactNode } from "react";

export interface HistoryListProps {
  entries: HistoryEntry[];
  onAdd: () => void;
  onSelectEntry: (id: number) => void;
  headerActions?: ReactNode;
  selectionMode?: boolean;
  selectedKeys?: Set<string>;
  onToggleSelection?: (entry: HistoryEntry) => void;
  titleLevel?: 1 | 2;
}

/**
 * "History" section. Either the unordered list of rows, or an empty state
 * with the "Add the first one" CTA — same as the legacy template.
 */
export default function HistoryList({
  entries,
  onAdd,
  onSelectEntry,
  headerActions,
  selectionMode = false,
  selectedKeys,
  onToggleSelection,
  titleLevel = 1,
}: HistoryListProps) {
  const Title = `h${titleLevel}` as const;

  return (
    <section className="page-section">
      <header
        className={
          "section-header" +
          (headerActions ? " section-header--with-actions" : "")
        }
      >
        <Title className="section-header__title">History</Title>
        {headerActions ? (
          <div className="section-header__actions">{headerActions}</div>
        ) : null}
      </header>

      {entries.length > 0 ? (
        <ul className="history-list" role="list">
          {entries.map((entry, index) => (
            <HistoryRow
              key={`${entry.entry_type}:${entry.id}`}
              entry={entry}
              index={index}
              onSelect={onSelectEntry}
              selectionMode={selectionMode}
              selected={selectedKeys?.has(`${entry.entry_type}:${entry.id}`) ?? false}
              onToggleSelection={onToggleSelection}
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          message="No history entries yet."
          ctaLabel="Add the first one"
          onCtaClick={onAdd}
        />
      )}
    </section>
  );
}
